import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Search, ChevronLeft, ChevronRight, ShieldCheck, Ban, UserCheck, UserX, Eye, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('admin_token');
const api = (method, url, data) => axios({ method, url: `${API_URL}/api${url}`, data, headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [detailUser, setDetailUser] = useState(null);

  const fetchUsers = async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (genderFilter) params.append('gender', genderFilter);
      if (planFilter) params.append('plan', planFilter);
      params.append('page', pg);
      params.append('limit', 15);
      const res = await api('get', `/admin/users?${params}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(pg);
    } catch (e) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAction = async () => {
    if (!actionDialog) return;
    try {
      await api('put', `/admin/users/${actionDialog.userId}/status`, { action: actionDialog.action, reason: actionReason });
      toast.success(`User ${actionDialog.action} successfully`);
      setActionDialog(null);
      setActionReason('');
      fetchUsers(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const calcAge = (dob) => { if (!dob) return '-'; return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000)); };

  const viewUser = async (userId) => {
    try {
      const res = await api('get', `/admin/users/${userId}`);
      setDetailUser(res.data);
    } catch (e) { toast.error('Failed to load user'); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#2D2424]">User Management</h1>
        <p className="text-sm text-[#78716C]">{total} total users</p>
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-xl border border-[#E7E5E4] bg-white mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1)}
                placeholder="Search name, phone, city..." className="pl-10 h-10 border-[#E7E5E4]" data-testid="admin-search-input" />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-10"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all_status">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger className="w-32 h-10"><SelectValue placeholder="Gender" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all_gender">All</SelectItem>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-32 h-10"><SelectValue placeholder="Plan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all_plans">All</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="platinum">Platinum</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => fetchUsers(1)} className="bg-[#D9381E] text-white rounded-lg h-10 px-5" data-testid="admin-search-btn">Search</Button>
        </div>
      </Card>

      {/* Users Table */}
      <Card className="rounded-xl border border-[#E7E5E4] bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-[#D9381E] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9F5F0] border-b border-[#E7E5E4]">
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Name</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Phone</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Age</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">City</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Plan</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Status</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">KYC</th>
                  <th className="text-left p-3 font-semibold text-[#2D2424]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-[#E7E5E4]/50 hover:bg-[#F9F5F0]/50 transition-colors">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#D9381E]/10 flex items-center justify-center text-xs font-bold text-[#D9381E]">{u.name?.[0]}</div>
                        <div>
                          <div className="font-medium text-[#2D2424]">{u.name}</div>
                          <div className="text-xs text-[#78716C]">{u.gender}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-[#78716C]">{u.phone || '-'}</td>
                    <td className="p-3 text-[#78716C]">{calcAge(u.dob)}</td>
                    <td className="p-3 text-[#78716C]">{u.city || '-'}</td>
                    <td className="p-3">
                      <Badge className={`text-[10px] ${u.subscription?.plan === 'gold' ? 'bg-[#F2A900]/20 text-[#F2A900]' : u.subscription?.plan === 'platinum' ? 'bg-purple-100 text-purple-700' : u.subscription?.plan === 'silver' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                        {u.subscription?.plan || 'free'}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={u.kyc_status === 'verified' ? 'bg-blue-100 text-blue-700' : 'bg-[#F9F5F0] text-[#78716C]'}>{u.kyc_status || 'unverified'}</Badge>
                    </td>
                    <td className="p-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`user-action-${u.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewUser(u.id)}><Eye className="w-3 h-3 mr-2" /> View Details</DropdownMenuItem>
                          {u.kyc_status !== 'verified' && (
                            <DropdownMenuItem onClick={() => setActionDialog({ userId: u.id, action: 'verify', userName: u.name })}>
                              <ShieldCheck className="w-3 h-3 mr-2" /> Verify KYC
                            </DropdownMenuItem>
                          )}
                          {u.kyc_status === 'verified' && (
                            <DropdownMenuItem onClick={() => setActionDialog({ userId: u.id, action: 'unverify', userName: u.name })}>
                              <UserX className="w-3 h-3 mr-2" /> Unverify
                            </DropdownMenuItem>
                          )}
                          {u.is_active ? (
                            <DropdownMenuItem onClick={() => setActionDialog({ userId: u.id, action: 'deactivate', userName: u.name })} className="text-orange-600">
                              <UserX className="w-3 h-3 mr-2" /> Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setActionDialog({ userId: u.id, action: 'activate', userName: u.name })} className="text-green-600">
                              <UserCheck className="w-3 h-3 mr-2" /> Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setActionDialog({ userId: u.id, action: 'ban', userName: u.name })} className="text-red-600">
                            <Ban className="w-3 h-3 mr-2" /> Ban User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={8} className="p-12 text-center text-[#78716C]">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#E7E5E4]">
            <span className="text-xs text-[#78716C]">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchUsers(page - 1)} disabled={page <= 1}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => fetchUsers(page + 1)} disabled={page >= pages}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => { setActionDialog(null); setActionReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm: {actionDialog?.action} {actionDialog?.userName}</DialogTitle>
          </DialogHeader>
          <Textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder="Reason (optional)" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={handleAction} className="bg-[#D9381E] text-white" data-testid="confirm-action-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>User Details</DialogTitle></DialogHeader>
          {detailUser && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[#78716C]">Name:</span> <span className="font-medium">{detailUser.user?.name}</span></div>
                <div><span className="text-[#78716C]">Phone:</span> <span className="font-medium">{detailUser.user?.phone}</span></div>
                <div><span className="text-[#78716C]">Gender:</span> <span className="font-medium">{detailUser.user?.gender}</span></div>
                <div><span className="text-[#78716C]">City:</span> <span className="font-medium">{detailUser.user?.city}</span></div>
                <div><span className="text-[#78716C]">Religion:</span> <span className="font-medium">{detailUser.user?.religion}</span></div>
                <div><span className="text-[#78716C]">Caste:</span> <span className="font-medium">{detailUser.user?.caste}</span></div>
                <div><span className="text-[#78716C]">Education:</span> <span className="font-medium">{detailUser.user?.education_degree}</span></div>
                <div><span className="text-[#78716C]">Occupation:</span> <span className="font-medium">{detailUser.user?.occupation}</span></div>
              </div>
              <div className="border-t pt-3 grid grid-cols-3 gap-3">
                <div className="text-center"><div className="text-lg font-bold text-[#D9381E]">{detailUser.activity?.interests_sent}</div><div className="text-xs text-[#78716C]">Sent</div></div>
                <div className="text-center"><div className="text-lg font-bold text-[#F2A900]">{detailUser.activity?.interests_received}</div><div className="text-xs text-[#78716C]">Received</div></div>
                <div className="text-center"><div className="text-lg font-bold text-green-600">{detailUser.activity?.messages_sent}</div><div className="text-xs text-[#78716C]">Messages</div></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
